import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertCircle,
  Gauge,
  HeartPulse,
  Loader2,
  Ruler,
  Save,
  Scale,
  Stethoscope,
  Thermometer,
  User,
  Wind,
  X,
} from 'lucide-react';
import { PatientVisitSelector } from '../../components/ui/patient-visit-selector';
import {
  useCreateVitals,
  useUpdateVitals,
} from '../../services/httpServices/queries';
import type { VitalSigns } from '../../services/httpServices/vitalsService';
import { joinFullName } from '../../utils/patientName';

interface VitalsFormModalProps {
  open: boolean;
  /** When supplied the modal edits this record instead of creating a new one. */
  record?: VitalSigns | null;
  onClose: () => void;
  /** Called after the API accepts the record (before the modal closes). */
  onSaved?: () => void;
}

interface FormState {
  temperature: string;
  pulse: string;
  respiratoryRate: string;
  spo2: string;
  bpSystolic: string;
  bpDiastolic: string;
  weight: string;
  height: string;
  painScore: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  temperature: '',
  pulse: '',
  respiratoryRate: '',
  spo2: '',
  bpSystolic: '',
  bpDiastolic: '',
  weight: '',
  height: '',
  painScore: '',
  notes: '',
};

/** Splits the stored `120/80` string back into its two inputs. */
function splitBloodPressure(value?: string | null): [string, string] {
  if (!value) return ['', ''];
  const [systolic, diastolic] = String(value).split('/');
  return [systolic?.trim() || '', diastolic?.trim() || ''];
}

function toFormState(record?: VitalSigns | null): FormState {
  if (!record) return EMPTY_FORM;
  const [bpSystolic, bpDiastolic] = splitBloodPressure(record.bloodPressure);

  const asText = (value?: number | null) =>
    value === null || value === undefined ? '' : String(value);

  return {
    temperature: asText(record.temperature),
    pulse: asText(record.pulse),
    respiratoryRate: asText(record.respiratoryRate),
    spo2: asText(record.spo2),
    bpSystolic,
    bpDiastolic,
    weight: asText(record.weight),
    height: asText(record.height),
    painScore: asText(record.painScore),
    notes: record.notes || '',
  };
}

/** Parse a text input to a number, ignoring blanks. */
function num(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Compact labelled input used throughout the vitals grid. */
function VitalField({
  icon: Icon,
  label,
  unit,
  hint,
  value,
  onChange,
  placeholder,
  min,
  max,
  step,
  autoFocus,
}: {
  icon: LucideIcon;
  label: string;
  unit?: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: string;
  autoFocus?: boolean;
}) {
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-text-muted mb-1">
        <Icon size={12} className="text-primary" />
        {label}
        {unit && <span className="normal-case tracking-normal text-text-muted/80">({unit})</span>}
      </span>
      <input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        autoFocus={autoFocus}
        className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-text placeholder:text-text-muted/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60"
      />
      {hint && <span className="mt-0.5 block text-[10px] text-text-muted/80">{hint}</span>}
    </label>
  );
}

export function VitalsFormModal({ open, record, onClose, onSaved }: VitalsFormModalProps) {
  const isEdit = Boolean(record);
  const createVitals = useCreateVitals();
  const updateVitals = useUpdateVitals();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [patientId, setPatientId] = useState('');
  const [visitId, setVisitId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isPending = createVitals.isPending || updateVitals.isPending;

  // Reset whenever the modal opens or switches between create/edit.
  useEffect(() => {
    if (!open) return;
    setForm(toFormState(record));
    setPatientId(record?.patientId || '');
    setVisitId(record?.visitId || '');
    setError(null);
  }, [open, record]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isPending) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, isPending, onClose]);

  const handlePatientChange = useCallback(
    ({ patientId: nextPatientId, visitId: nextVisitId }: { patientId: string; visitId: string }) => {
      setPatientId(nextPatientId);
      setVisitId(nextVisitId);
    },
    [],
  );

  const setField = (key: keyof FormState) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const bmi = useMemo(() => {
    const weight = Number(form.weight);
    const height = Number(form.height);
    if (!weight || !height) return null;
    const meters = height / 100;
    if (meters <= 0) return null;
    return Number((weight / (meters * meters)).toFixed(1));
  }, [form.weight, form.height]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!isEdit && (!patientId || !visitId)) {
      setError('Select a patient with an active visit before saving.');
      return;
    }

    const systolic = num(form.bpSystolic);
    const diastolic = num(form.bpDiastolic);

    if ((systolic && !diastolic) || (!systolic && diastolic)) {
      setError('Enter both systolic and diastolic blood pressure values.');
      return;
    }
    if (systolic && diastolic && systolic <= diastolic) {
      setError('Systolic pressure must be higher than diastolic pressure.');
      return;
    }

    const pain = num(form.painScore);
    if (pain !== undefined && (pain < 0 || pain > 10)) {
      setError('Pain score must be between 0 and 10.');
      return;
    }

    const shared = {
      temperature: num(form.temperature),
      temperatureUnit: 'C',
      pulse: num(form.pulse),
      spo2: num(form.spo2),
      respiratoryRate: num(form.respiratoryRate),
      bloodPressure: systolic && diastolic ? `${systolic}/${diastolic}` : undefined,
      weight: num(form.weight),
      weightUnit: 'kg',
      height: num(form.height),
      heightUnit: 'cm',
      painScore: pain,
      notes: form.notes.trim() || undefined,
    };

    try {
      if (record) {
        // Editing sends `null` for cleared inputs so the stored value is removed.
        await updateVitals.mutateAsync({
          id: record.id,
          data: {
            temperature: num(form.temperature) ?? null,
            temperatureUnit: 'C',
            pulse: num(form.pulse) ?? null,
            spo2: num(form.spo2) ?? null,
            respiratoryRate: num(form.respiratoryRate) ?? null,
            bloodPressure: systolic && diastolic ? `${systolic}/${diastolic}` : null,
            weight: num(form.weight) ?? null,
            weightUnit: 'kg',
            height: num(form.height) ?? null,
            heightUnit: 'cm',
            painScore: pain ?? null,
            notes: form.notes.trim() || null,
          },
        });
      } else {
        await createVitals.mutateAsync({ ...shared, patientId, visitId });
      }
      onSaved?.();
      onClose();
    } catch {
      // The global mutation handler renders the API error toast.
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit vitals' : 'Record vitals'}
      onClick={() => {
        if (!isPending) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <HeartPulse size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-text">
                {isEdit ? 'Edit Vitals' : 'Record Vitals'}
              </h2>
              <p className="text-xs text-text-muted">
                {isEdit
                  ? 'Update the captured readings for this record.'
                  : 'Capture a new set of patient observations.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 overflow-y-auto p-4">
          {isEdit && record ? (
            <div className="flex items-center gap-3 rounded-xl border border-border bg-background p-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                <User size={16} className="text-primary" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-text">
                  {joinFullName(record.patient || {}) || 'Unknown patient'}
                </p>
                <p className="truncate text-xs text-text-muted">
                  {record.patient?.registrationNumber || '—'}
                  {record.visit?.visitNumber ? ` · ${record.visit.visitNumber}` : ''}
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-background p-3">
              <PatientVisitSelector
                patientId={patientId}
                visitId={visitId}
                onChange={handlePatientChange}
                compact
              />
            </div>
          )}

          <section className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Stethoscope size={13} className="text-primary" />
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                Vital Signs
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <VitalField
                icon={Thermometer}
                label="Temperature"
                unit="°C"
                value={form.temperature}
                onChange={setField('temperature')}
                placeholder="36.8"
                min={25}
                max={45}
                step="0.1"
              />
              <VitalField
                icon={HeartPulse}
                label="Pulse"
                unit="bpm"
                value={form.pulse}
                onChange={setField('pulse')}
                placeholder="72"
                min={20}
                max={300}
              />
              <VitalField
                icon={Wind}
                label="Resp. Rate"
                unit="/min"
                value={form.respiratoryRate}
                onChange={setField('respiratoryRate')}
                placeholder="16"
                min={1}
                max={100}
              />
              <VitalField
                icon={Activity}
                label="SpO₂"
                unit="%"
                value={form.spo2}
                onChange={setField('spo2')}
                placeholder="98"
                min={0}
                max={100}
              />
              <VitalField
                icon={Scale}
                label="Weight"
                unit="kg"
                value={form.weight}
                onChange={setField('weight')}
                placeholder="70"
                min={1}
                max={500}
                step="0.1"
              />
              <VitalField
                icon={Ruler}
                label="Height"
                unit="cm"
                value={form.height}
                onChange={setField('height')}
                placeholder="170"
                min={20}
                max={300}
                step="0.1"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* Blood pressure keeps both halves on one row. */}
              <div>
                <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-text-muted">
                  <Activity size={12} className="text-primary" />
                  Blood Pressure
                  <span className="normal-case tracking-normal text-text-muted/80">(mmHg)</span>
                </span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={form.bpSystolic}
                    onChange={(e) => setField('bpSystolic')(e.target.value)}
                    placeholder="120"
                    min={40}
                    max={300}
                    aria-label="Systolic"
                    className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-center text-sm text-text placeholder:text-text-muted/70 focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <span className="text-xs font-medium text-text-muted">/</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={form.bpDiastolic}
                    onChange={(e) => setField('bpDiastolic')(e.target.value)}
                    placeholder="80"
                    min={20}
                    max={200}
                    aria-label="Diastolic"
                    className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-center text-sm text-text placeholder:text-text-muted/70 focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <span className="mt-0.5 block text-[10px] text-text-muted/80">
                  Systolic / Diastolic
                </span>
              </div>

              <VitalField
                icon={Gauge}
                label="Pain Score"
                unit="0–10"
                value={form.painScore}
                onChange={setField('painScore')}
                placeholder="0"
                min={0}
                max={10}
                hint="0 = no pain, 10 = worst pain"
              />
            </div>
          </section>

          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                Notes
              </span>
              {bmi !== null && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                  BMI {bmi}
                </span>
              )}
            </div>
            <textarea
              value={form.notes}
              onChange={(e) => setField('notes')(e.target.value)}
              rows={2}
              placeholder="Optional remarks — symptoms, position, device used…"
              className="w-full resize-none rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-text placeholder:text-text-muted/70 focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
            ></textarea>
          </section>

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-danger/20 bg-danger/10 p-2.5 text-xs text-danger">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg px-3 py-1.5 text-sm text-text-muted transition-colors hover:text-text disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending || (!isEdit && (!patientId || !visitId))}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {isEdit ? 'Save Changes' : 'Record Vitals'}
          </button>
        </div>
      </form>
    </div>
  );
}
